import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card } from "@/components/dashboard/ErpWidgets";
import { Money } from "@/components/Money";
import { TileBadge } from "@/components/filters/TileBadge";
import { monthLabel, omr } from "@/lib/format";
import { moneyNode } from "@/components/Money";
import { cn } from "@/lib/utils";

const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);
const axis = { tick: { fontSize: 11, fill: "#64748B" }, tickLine: false };

export const SummaryCards = ({ s, status }) => (
  <div className="grid gap-4 sm:grid-cols-4 xl:grid-cols-7" data-testid="sales-summary-cards">
    {[["total_sales", "Total sales"], ["returns", "Returns"], ["net_sales", "Net sales"], ["cost", "Cost (COGS)"], ["gp", "Gross profit (GP)"], ["gp_margin_pct", "GP margin"], ["invoices", "Invoices"]].map(([k, l]) => (
      <div key={k} className="rise-in rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="text-xs text-slate-500">{l}</div>
        <div className="mt-1 text-lg font-semibold text-slate-900" data-testid={`sales-card-${k}`}>
          {k === "invoices" ? (s?.[k] ?? 0).toLocaleString() : k === "gp_margin_pct" ? (s?.[k] == null ? "—" : `${s[k].toFixed(2)}%`) : <Money value={s?.[k] ?? 0} profit={k === "gp"} />}
        </div>
        <div className="mt-1.5"><TileBadge status={k.startsWith("gp") ? status?.gp : k === "cost" ? status?.cost : status?.sales} testid={`sales-badge-${k}`} /></div>
      </div>
    ))}
  </div>
);

export const SalesTrendChart = ({ data, granularity, onGranularity }) => (
  <Card title="Sales trend" subtitle="Net sales (ex-VAT, returns netted) · follows the filters" testid="sales-trend-chart"
    actions={<div className="flex rounded-md border border-slate-200 p-0.5">{["daily", "monthly"].map((g) => (
      <button key={g} type="button" onClick={() => onGranularity(g)} data-testid={`sales-trend-${g}`}
        className={cn("rounded px-2.5 py-1 text-xs capitalize", granularity === g ? "bg-slate-900 text-white" : "text-slate-600")}>{g}</button>))}</div>}>
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={(data || []).map((p) => ({ ...p, label: granularity === "monthly" ? monthLabel(p.period, true) : p.period.slice(5) }))}>
          <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" {...axis} minTickGap={16} /><YAxis tickFormatter={kfmt} {...axis} axisLine={false} width={48} />
          <Tooltip formatter={(v) => moneyNode(v)} />
          <Line type="monotone" dataKey="amount" name="Net sales" stroke="#0D9488" strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="gp" name="Gross profit (GP)" stroke="#0F172A" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

export const TopBars = ({ title, data, onPick, testid, color }) => (
  <Card title={title} subtitle="Top 10 by net sales · click a bar to filter" testid={testid}>
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data || []} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={kfmt} {...axis} />
          <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 10.5, fill: "#334155" }} tickLine={false} tickFormatter={(v) => (v.length > 26 ? `${v.slice(0, 25)}…` : v)} />
          <Tooltip formatter={(v) => moneyNode(v)} />
          <Bar dataKey="amount" name="Net sales" fill={color} cursor="pointer" isAnimationActive={false} onClick={(d) => onPick(d.name)} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);
